/**
 * The signed-in user, remembered across reloads.
 *
 * WHY
 * ---
 * `AuthContext` used to hold the user only in React state and re-derive it from
 * `GET /auth/verify` on every boot. Offline, that request simply fails - and
 * because *any* failure was treated as an invalid token, the provider signed the
 * cashier out, wiped the token and dropped the app on the login screen. On a
 * till with no connection that means no catalogue, no invoices, nothing: the
 * exact opposite of what an offline-capable POS is for.
 *
 * So the last verified profile is cached, and only an *authoritative* rejection
 * (401/403 from the server) ends the session. A dropped link keeps the session
 * and the shell shows the offline indicator instead.
 *
 * Nothing here is a security decision: the token is still the only credential,
 * every request still carries it, and the server still enforces ACLs. Caching
 * the profile only decides which shell the operator sees while the link is down.
 */

export const AUTH_USER_CACHE_KEY = 'dira360:auth:user:v1';

/** A profile cached longer ago than this is ignored (forces a real verify). */
export const AUTH_USER_CACHE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/** Memoised scope, so the request path does not re-parse storage on every GET. */
let scopeMemo = null;

// Decode only to bind a cached profile to the token's user and expiration. This
// is not signature verification and must never grant server-side permissions.
const readTokenClaims = (token) => {
  try {
    const payload = String(token || '').split('.')[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')));
  } catch (_) { return null; }
};

export const isCachedUserForToken = (user, token, now = Date.now()) => {
  const claims = readTokenClaims(token);
  const cachedId = user?.id ?? user?.user_id;
  const tokenId = claims?.userId ?? claims?.user_id ?? claims?.sub;
  return cachedId != null && tokenId != null && String(cachedId) === String(tokenId)
    && Number.isFinite(Number(claims.exp)) && Number(claims.exp) * 1000 > now;
};

const readStorage = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage.getItem(AUTH_USER_CACHE_KEY);
  } catch (_) {
    // Private mode / storage disabled: no cache, everything still works online.
    return null;
  }
};

/**
 * @returns {null | {user: object, savedAt: string, ageMs: number, stale: boolean}}
 */
export const readCachedUser = ({ now = Date.now() } = {}) => {
  const raw = readStorage();
  if (!raw) return null;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (_) {
    return null;
  }

  const user = parsed?.user;
  if (!user || typeof user !== 'object') return null;

  const savedAtMs = Number(parsed.savedAtMs) || Date.parse(parsed.savedAt || '');
  if (!Number.isFinite(savedAtMs)) return null;

  const ageMs = Math.max(0, now - savedAtMs);
  if (ageMs > AUTH_USER_CACHE_MAX_AGE_MS) return null;

  return {
    user,
    savedAt: parsed.savedAt || new Date(savedAtMs).toISOString(),
    ageMs,
    stale: ageMs > 24 * 60 * 60 * 1000
  };
};

/** Remember the profile the server just confirmed (login or successful verify). */
export const saveCachedUser = (user, { now = Date.now() } = {}) => {
  if (!user || typeof user !== 'object') return null;
  const record = {
    user,
    savedAt: new Date(now).toISOString(),
    savedAtMs: now
  };
  try {
    window.localStorage.setItem(AUTH_USER_CACHE_KEY, JSON.stringify(record));
  } catch (_) {
    return null;
  }
  scopeMemo = null;
  return record;
};

/** Forget the cached profile (real sign-out, or a token the server rejected). */
export const clearCachedUser = () => {
  scopeMemo = null;
  try {
    window.localStorage.removeItem(AUTH_USER_CACHE_KEY);
  } catch (_) {
    // Nothing to clear.
  }
};

/**
 * Identity used to namespace cached reads.
 *
 * Two people sharing a till must never see each other's invoices, so the read
 * cache key carries this scope. `anon` means "no cached profile", in which case
 * cached reads are not served at all - safer than showing someone else's data.
 */
export const currentUserScope = () => {
  const cached = readCachedUser();
  const user = cached?.user;
  let token = '';
  try { token = window.localStorage.getItem('token') || ''; } catch (_) { return 'anon'; }
  if (!isCachedUserForToken(user, token)) return 'anon';
  const identity = user?.id ?? user?.user_id ?? user?.email ?? user?.username ?? null;
  scopeMemo = identity == null ? 'anon' : `u:${String(identity)}`;
  return scopeMemo;
};

/** Test seam: forget the memoised scope. */
export const resetUserScopeMemo = () => {
  scopeMemo = null;
};

/**
 * Does this failure mean the session is genuinely over?
 *
 * Only a server that answered with 401 (token rejected) or 403 (account/role
 * revoked) does. A timeout, a `ERR_NETWORK` or a 5xx means "we cannot tell right
 * now", which must keep the session alive.
 */
export const isSessionEndingError = (error) => {
  if (!error) return false;
  const status = Number(error?.response?.status || 0);
  if (status === 401 || status === 403) return true;
  return error.isAccessDenied === true;
};
